import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, SORT } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './user-preferences.reducer';

export const UserPreferences = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(overrideSortStateWithQueryParams(getSortState(location, 'id'), location.search));

  const userPreferencesList = useAppSelector(state => state.userPreferences.entities);
  const loading = useAppSelector(state => state.userPreferences.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="user-preferences-heading" data-cy="UserPreferencesHeading">
        <Translate contentKey="reachamyApp.userPreferences.home.title">User Preferences</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="reachamyApp.userPreferences.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/user-preferences/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="reachamyApp.userPreferences.home.createLabel">Create new User Preferences</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {userPreferencesList && userPreferencesList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="reachamyApp.userPreferences.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('userID')}>
                  <Translate contentKey="reachamyApp.userPreferences.userID">User ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('userID')} />
                </th>
                <th className="hand" onClick={sort('profilePic')}>
                  <Translate contentKey="reachamyApp.userPreferences.profilePic">Profile Pic</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('profilePic')} />
                </th>
                <th className="hand" onClick={sort('contactEmail')}>
                  <Translate contentKey="reachamyApp.userPreferences.contactEmail">Contact Email</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contactEmail')} />
                </th>
                <th className="hand" onClick={sort('country')}>
                  <Translate contentKey="reachamyApp.userPreferences.country">Country</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('country')} />
                </th>
                <th className="hand" onClick={sort('bio')}>
                  <Translate contentKey="reachamyApp.userPreferences.bio">Bio</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bio')} />
                </th>
                <th className="hand" onClick={sort('darkMode')}>
                  <Translate contentKey="reachamyApp.userPreferences.darkMode">Dark Mode</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('darkMode')} />
                </th>
                <th className="hand" onClick={sort('personalizeAdsBasedOnInfoFromOurPartners')}>
                  <Translate contentKey="reachamyApp.userPreferences.personalizeAdsBasedOnInfoFromOurPartners">
                    Personalize Ads Based On Info From Our Partners
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('personalizeAdsBasedOnInfoFromOurPartners')} />
                </th>
                <th className="hand" onClick={sort('personalizeRecommendationsBasedOnYourGeneralLocation')}>
                  <Translate contentKey="reachamyApp.userPreferences.personalizeRecommendationsBasedOnYourGeneralLocation">
                    Personalize Recommendations Based On Your General Location
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('personalizeRecommendationsBasedOnYourGeneralLocation')} />
                </th>
                <th className="hand" onClick={sort('notificationsInboxMessages')}>
                  <Translate contentKey="reachamyApp.userPreferences.notificationsInboxMessages">Notifications Inbox Messages</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notificationsInboxMessages')} />
                </th>
                <th className="hand" onClick={sort('notificationsEmailMessages')}>
                  <Translate contentKey="reachamyApp.userPreferences.notificationsEmailMessages">Notifications Email Messages</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notificationsEmailMessages')} />
                </th>
                <th className="hand" onClick={sort('notificationsUpvotesOnYourPosts')}>
                  <Translate contentKey="reachamyApp.userPreferences.notificationsUpvotesOnYourPosts">
                    Notifications Upvotes On Your Posts
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notificationsUpvotesOnYourPosts')} />
                </th>
                <th className="hand" onClick={sort('notificationsRepliesToYourComments')}>
                  <Translate contentKey="reachamyApp.userPreferences.notificationsRepliesToYourComments">
                    Notifications Replies To Your Comments
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notificationsRepliesToYourComments')} />
                </th>
                <th className="hand" onClick={sort('notificationsActivityOnYourPosts')}>
                  <Translate contentKey="reachamyApp.userPreferences.notificationsActivityOnYourPosts">
                    Notifications Activity On Your Posts
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notificationsActivityOnYourPosts')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {userPreferencesList.map((userPreferences, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/user-preferences/${userPreferences.id}`} color="link" size="sm">
                      {userPreferences.id}
                    </Button>
                  </td>
                  <td>{userPreferences.userID}</td>
                  <td>{userPreferences.profilePic}</td>
                  <td>{userPreferences.contactEmail}</td>
                  <td>{userPreferences.country}</td>
                  <td>{userPreferences.bio}</td>
                  <td>{userPreferences.darkMode ? 'true' : 'false'}</td>
                  <td>{userPreferences.personalizeAdsBasedOnInfoFromOurPartners ? 'true' : 'false'}</td>
                  <td>{userPreferences.personalizeRecommendationsBasedOnYourGeneralLocation ? 'true' : 'false'}</td>
                  <td>{userPreferences.notificationsInboxMessages ? 'true' : 'false'}</td>
                  <td>{userPreferences.notificationsEmailMessages ? 'true' : 'false'}</td>
                  <td>{userPreferences.notificationsUpvotesOnYourPosts ? 'true' : 'false'}</td>
                  <td>{userPreferences.notificationsRepliesToYourComments ? 'true' : 'false'}</td>
                  <td>{userPreferences.notificationsActivityOnYourPosts ? 'true' : 'false'}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/user-preferences/${userPreferences.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/user-preferences/${userPreferences.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/user-preferences/${userPreferences.id}/delete`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="reachamyApp.userPreferences.home.notFound">No User Preferences found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default UserPreferences;
